/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import {
  makeStyles,
  Modal,
  Button,
  IconButton,
  Divider,
  Box,
  Typography,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import {
  errorSnackbar,
  successSnackbar,
} from "../../redux/features/customSnackbar/action";
import { API } from "../../apis/api";
import { filterFutureTime, validateFile } from "../../utils/utils";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "8px",
    boxShadow: theme.shadows[5],
    padding: 0,
    width: "800px",
    maxWidth: "90vw",
    outline: "none",
    maxHeight: "90vh",
    overflow: "auto",
  },
  header: {
    padding: theme.spacing(3, 4),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontWeight: "bold",
    fontSize: "2rem",
  },
  textXs: {
    fontFamily: "Open Sans",
    fontWeight: "600",
    fontSize: "16px",
  },
  content: {
    padding: theme.spacing(3, 4),
  },
  formGroup: {
    marginBottom: theme.spacing(3),
  },
  formLabel: {
    fontWeight: "bold",
    color: "#054f96",
    marginBottom: theme.spacing(1),
    display: "block",
  },
  fileInput: {
    display: "none",
  },
  formInput: {
    width: "100%",
  },
  fileInputLabel: {
    display: "flex",
    border: "1px solid #ccc",
    borderRadius: "4px",
    overflow: "hidden",
    cursor: "pointer",
  },
  modalContent: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  dateInput: {
    width: "100%",
  },
  fileInputText: {
    padding: theme.spacing(2),
    flexGrow: 1,
    color: "#888",
  },
  browseButton: {
    backgroundColor: "#e0e0e0",
    color: "#000",
    padding: theme.spacing(2, 4),
    borderRadius: 0,
    "&:hover": {
      backgroundColor: "#d0d0d0",
    },
  },
  previewContainer: {
    marginTop: theme.spacing(2),
    display: "grid",
    alignItems: "center",
    gridTemplateColumns: "1fr 1fr",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  previewBox: {
    width: "160px",
    height: "120px",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    backgroundColor: "#fff",
    "&:hover": {
      boxShadow: theme.shadows[2],
    },
  },
  previewIcon: {
    color: "#054f96",
    fontSize: "2.5rem",
  },
  previewFileName: {
    textAlign: "center",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  deletePreviewButton: {
    position: "absolute",
    top: -15,
    right: -15,
    backgroundColor: "#e0e0e0",
    padding: "2px",
    "&:hover": {
      backgroundColor: "#d0d0d0",
    },
  },
  actionButtons: {
    padding: theme.spacing(3, 4),
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
  },
  cancelButton: {
    backgroundColor: "#555",
    color: "white",
    padding: theme.spacing(1.5, 4),
    "&:hover": {
      backgroundColor: "#444",
    },
  },
  uploadButton: {
    backgroundColor: "#00205b",
    color: "white",
    padding: theme.spacing(1.5, 4),
    "&:hover": {
      backgroundColor: "#001845",
    },
  },
  message: {
    marginTop: theme.spacing(2),
  },
}));

const CustomDateTimeInput = React.forwardRef(
  ({ value, onClick, className }, ref) => (
    <TextField
      ref={ref}
      variant="outlined"
      fullWidth
      value={value || ""}
      onClick={onClick}
      placeholder="DD/MM/YYYY - HH:MM"
      InputProps={{
        readOnly: true,
      }}
      className={className} // <- This ensures it inherits styles like width: 100%
    />
  )
);


function UpdateELModal({ open, onClose, onUpload, rowData, handleRefresh }) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const userId= useSelector((state)=>state.auth.user.userId)
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    files: [],
    courier_vendor: "",
    date: null,
    tracking_id: "",
  });

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (event) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const newFiles = Array.from(event.target.files);
    const validFiles = [];
    const errors = [];

    newFiles.forEach((file) => {
      try {
        validateFile(file);
        validFiles.push(file);
      } catch (error) {
        errors.push(error.message);
      }
    });

    if (errors.length > 0) {
      setUploadError(errors.join(" "));
      setUploadSuccess("");
    } else {
      setUploadError("");
      setUploadSuccess("Files uploaded successfully.");
    }

    if (validFiles.length > 0) {
      setFormData((prev) => ({
        ...prev,
        files: [...(prev.files || []), ...validFiles],
      }));
    }

    // Clear file input using the ref instead of modifying event
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index) => {
    const newFiles = [...formData.files];
    newFiles.splice(index, 1);
    setFormData((prev) => ({ ...prev, files: newFiles }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, date }));
  };

  const uploadELToAPI = async () => {
    const uploadData = new FormData();
    formData.files.forEach((file) => {
      uploadData.append("file", file);
    });
    uploadData.append("courier_vendor", formData.courier_vendor);
    uploadData.append("tracking_id", formData.tracking_id);
    uploadData.append("ticket_code", rowData.ticketNumber);
    uploadData.append("date_time_courier", formData.date?.toISOString());
    uploadData.append("document_type", "entitlement_letter");
    uploadData.append("sub",userId)

    const response = await API.post("/document/upload-document/", uploadData, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  };

  const handleUpload = async () => {
    if (formData.files.length === 0) {
      setUploadError("Please upload at least one file.");
      return;
    }
    if (!formData.courier_vendor) {
      setUploadError("Please select a courier vendor.");
      return;
    }
    if (!formData.date) {
      setUploadError("Please select a date.");
      return;
    }
    if (!formData.tracking_id.trim()) {
      setUploadError("Please enter a tracking ID.");
      return;
    }

    try {
      setIsUploading(true);
      setUploadError("");
      setUploadSuccess("");

      await uploadELToAPI();
      dispatch(successSnackbar({
        title:'Your EL update successfully',
        subtitle:`Your EL update successfully of ticket no ${rowData.ticketNumber}`,
        duration:5000
      }))

      setUploadSuccess("EL updated successfully.");
      if (onUpload) onUpload(formData);
      if (handleRefresh) await handleRefresh();
      setTimeout(() => {
        setFormData({
          files: [],
          courier_vendor: "",
          date: null,
          tracking_id: "",
        });
        setUploadSuccess("");
        onClose();
      }, 2000);
    } catch (error) {
      dispatch(errorSnackbar({
        title:'Your EL update failed',
        subtitle:`Your EL update failed of ticket no. ${rowData.ticketNumber}`,
        duration:5000,
      }))
      setUploadError(
        error?.response?.data?.message ||
          error?.message ||
          "Upload failed. Please try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <Modal
      className={classes.modal}
      open={open}
      onClose={onClose}
      aria-labelledby="upload-document-modal"
    >
      <div className={classes.paper}>
        <div className={classes.header}>
          <div>
            <Typography className={classes.title} variant="h2">
              Update EL
            </Typography>
            <Typography className={classes.textXs}>
              Ticket no -{rowData.ticketNumber}
            </Typography>
          </div>
          <IconButton
            onClick={onClose}
            aria-label="close"
            disabled={isUploading}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <Divider />

        <div className={classes.content}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <div className={classes.formGroup}>
                <Typography className={classes.formLabel}>
                  Courier Image *
                </Typography>
                <input
                  accept="image/*"
                  className={classes.fileInput}
                  id="courier-image-upload"
                  type="file"
                  ref={fileInputRef}
                  name="files"
                  multiple
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
                <label
                  htmlFor="courier-image-upload"
                  className={classes.fileInputLabel}
                >
                  <div className={classes.fileInputText}>
                    {formData.files.length > 0
                      ? `${formData.files.length} file(s) selected`
                      : "Choose files..."}
                  </div>
                  <Button component="span" className={classes.browseButton}>
                    Browse
                  </Button>
                </label>

                {formData.files.length > 0 && (
                  <div className={classes.previewContainer}>
                    {formData.files.map((file, index) => (
                      <div key={index} className={classes.previewBox}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                        >
                          <FileCopyIcon className={classes.previewIcon} />
                          <Typography
                            className={classes.previewFileName}
                            title={`${file.name} (${formatFileSize(
                              file.size
                            )})`}
                          >
                            {file.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{ fontSize: "0.6rem" }}
                          >
                            {formatFileSize(file.size)}
                          </Typography>
                        </Box>
                        <IconButton
                          className={classes.deletePreviewButton}
                          size="small"
                          onClick={() => handleRemoveFile(index)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography className={classes.formLabel}>
                Courier Vendor *
              </Typography>
              <FormControl variant="outlined" className={classes.formInput}>
                <Select
                  name="courier_vendor"
                  value={formData.courier_vendor}
                  onChange={handleChange}
                  required
                  disabled={isUploading}
                >
                  <MenuItem value="e-cart">E-Cart</MenuItem>
                  <MenuItem value="blue-dart">Blue Dart</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <div className={classes.formGroup}>
                <Typography className={classes.formLabel}>
                  Date and Time of Courier *
                </Typography>
                <div className={classes.modalContent}>
                  <DatePicker
                    selected={formData.date}
                    onChange={handleDateChange}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={1}
                    maxDate={new Date()}
                    dateFormat="dd/MM/yyyy - HH:mm"
                    filterTime={filterFutureTime}
                    customInput={
                      <CustomDateTimeInput className={classes.formInput} />
                    }
                    disabled={isUploading}
                  />
                </div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6}>
              <div className={classes.formGroup}>
                <Typography className={classes.formLabel}>
                  Tracking ID *
                </Typography>
                <TextField
                  variant="outlined"
                  name="tracking_id"
                  value={formData.tracking_id}
                  onChange={handleChange}
                  className={classes.formInput}
                  disabled={isUploading}
                  required
                />
              </div>
            </Grid>
          </Grid>

          {uploadError && (
            <Typography className={classes.message} style={{ color: "red" }}>
              {uploadError}
            </Typography>
          )}
          {uploadSuccess && (
            <Typography className={classes.message} style={{ color: "green" }}>
              {uploadSuccess}
            </Typography>
          )}
        </div>

        <div className={classes.actionButtons}>
          <Button
            className={classes.cancelButton}
            variant="contained"
            onClick={onClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            className={classes.uploadButton}
            variant="contained"
            onClick={handleUpload}
            disabled={isUploading}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
UpdateELModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func,
  rowData: PropTypes.arrayOf(PropTypes.object),
};

export default UpdateELModal;
